/**
 * Aged Care suggestion engine — entity funding gate (Phase 2).
 * Spec §9.1.2: default exclude entity assets from automatic RAD funding suggestions.
 */
import { scenarioLivingExpensesOr } from '../../../shared/src/calculation/support/livingExpenses.ts';
import { ASSET_TYPES } from '../constants/index.ts';
import { isEntityOwnership } from '../../../shared/src/calculation/constants/clientStructureTypes.ts';
import { getInvestmentPropertyTaxSummary } from '../../../shared/src/calculation/entityDisposalTax.ts';
import type { AgedCareCosts } from '../../../shared/src/calculation/agedCareCostTypes.ts';
import { applyRadFundingToFactFind as applyManualRadFundingToFactFind } from '../strategy/StrategyIntegrationUtils.ts';
import {
    ENTITY_RAD_FUNDING_ASSET_TYPE_IDS,
    computeRadFundingTotals,
    isAssetEligibleForSuggestionRadFunding,
    isStructureFundingIncludedInSuggestions,
} from './entityFundingUtils.ts';
import {
    applyInvestmentPropertySaleToFactFind,
    routesIpProceedsToEntityPool,
} from './entityInvestmentPropertySale.ts';
import type { InvestmentPropertySaleDetails } from './entityInvestmentPropertySale.ts';
import { getEntityIdFromOwnership } from '../../../shared/src/calculation/constants/clientStructureTypes.ts';
import { getRecommendedEmergencyFund } from './emergencyFund.ts';
import type { FactFind, FactFindAsset } from '../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

export { isStructureFundingIncludedInSuggestions, isAssetEligibleForSuggestionRadFunding };

type DraftAsset = FactFindAsset;

/** One liquidation instruction inside a RAD-funding map (`{ [assetType]: LiquidationEntry[] }`). */
export interface LiquidationEntry {
    id?: string | number;
    amount?: number;
}

export type RadFundingMap = Record<string, LiquidationEntry[] | LiquidationEntry | undefined>;

/**
 * Ephemeral "what-if" override bag threaded through a scenario. Not modelled in
 * shared/factFindTypes.ts (its own doc comment calls that a separate effort) — this captures only
 * the fields this module actually reads/writes.
 */
export interface StrategyOptionsBag {
    sellInvestmentProperty?: boolean;
    investmentPropertyDetails?: InvestmentPropertySaleDetails;
    annualDap?: number;
    downsizeHome?: boolean;
    scenarioLivingExpenses?: number | string;
    structureFundingIncluded?: boolean;
    rentHome?: boolean;
    rentAmount?: number | string;
    rentExpenses?: number | string;
    [key: string]: unknown;
}

/** A candidate RAD-funding scenario, as produced/consumed by the aged care suggestion engine. */
export interface RadFundingScenario {
    /** A change of home (8 Oct 2026): what this candidate pays up front for the NEW room (the refund counts first). */
    radTarget?: number;
    /** A change of home: the "stay where you are" comparison row. */
    stayPut?: boolean;
    name?: string;
    groupKey?: string;
    fundingOrderLabel?: string;
    radFunding?: RadFundingMap;
    strategyOptions?: StrategyOptionsBag;
    [key: string]: unknown;
}

interface SuggestionEngineScenarioContext {
    radAmount: number;
    agedCareCosts?: AgedCareCosts;
    cleanCoupleValidation?: { partnerAnnualRent?: number; [key: string]: unknown };
    MPIR: number;
    radPercentagesSubset?: number[];
}

/** One liquid asset available to the suggestion engine (bank/term deposit/shares/ABP row). */
export interface AvailableSuggestionAsset {
    type: string;
    id: string | number | undefined;
    description?: string;
    value: number;
    growthRate: number;
    incomeRate: number;
    fullValue: number;
}

// Emergency fund the SE protects: the CLIENT'S chosen amount (factFind.emergencyFund, set from the SE settings)
// when provided, otherwise the recommended default (greater of 6 months' expenses or the floor). So a buffer the
// client enters always wins; we only fall back to the recommendation when they haven't set one.
export function resolveSeEmergencyFund(factFind: DraftFactFind | undefined): number {
    const v = factFind?.emergencyFund;
    if (v != null && v !== '') return Math.max(0, parseFloat(String(v)) || 0);
    return getRecommendedEmergencyFund(factFind);
}

/** Entity-owned IPs eligible for whole-asset sale scenarios (trust/company, not partnership). */
export function getEntityInvestmentPropertiesForSuggestions(factFind: DraftFactFind): DraftAsset[] {
    if (!isStructureFundingIncludedInSuggestions(factFind)) return [];
    const ips = factFind?.assets?.investmentProperty || [];
    return ips.filter(
        (ip) => isEntityOwnership(ip.ownership) && routesIpProceedsToEntityPool(factFind, ip)
    );
}

/**
 * Every investment property the engine may propose selling — PERSONALLY owned as well as
 * entity-owned.
 *
 * WHY THIS EXISTS. The engine could only ever sell an investment property held in a trust or
 * company, because the candidate list was built from the entity gate alone. A client who personally
 * owns a $900,000 rental and needs to fund an $800,000 deposit was never shown the most obvious
 * recommendation in the case — while Manual Control offered it on the same screen, one tick box
 * away. That is not a missing feature so much as a hole: selling an investment property to fund a
 * deposit is among the most common things an adviser recommends here, and the property is a fully
 * counted, fully taxed asset being converted into one that is exempt from the pension assets test
 * and refundable to the estate.
 *
 * Nothing below the candidate list needed changing. `getInvestmentPropertyTaxSummary` already
 * computes CGT with the discount for a personally-held property, and
 * `creditInvestmentPropertyUnallocatedProceeds` already routes leftover proceeds to the client's own
 * accounts when the seller is not an entity — both are the same functions Manual Control uses.
 *
 * The entity gate still applies to entity-owned properties only: excluding a trust's assets from
 * automatic suggestions is a deliberate setting about SOMEONE ELSE'S money, and says nothing about
 * a property the client owns outright.
 */
export function getSaleableInvestmentPropertiesForSuggestions(
    factFind: DraftFactFind,
): Array<{ property: DraftAsset; isEntity: boolean }> {
    const ips = factFind?.assets?.investmentProperty || [];
    const personal = ips
        .filter((ip) => !isEntityOwnership(ip.ownership) && (Number(ip.value) || 0) > 0)
        .map((property) => ({ property, isEntity: false }));
    const entity = getEntityInvestmentPropertiesForSuggestions(factFind).map((property) => ({ property, isEntity: true }));
    return [...personal, ...entity];
}

/**
 * Liquid assets the suggestion engine may use toward RAD (respects exclude/include gate).
 */
export function collectLiquidAssetsForSuggestionRad(
    factFind: DraftFactFind,
    emergencyFundLimit: number = resolveSeEmergencyFund(factFind),
): AvailableSuggestionAsset[] {
    const availableAssets: AvailableSuggestionAsset[] = [];

    ASSET_TYPES.forEach((type) => {
        if (!type.isLiquid) return;
        if (
            !ENTITY_RAD_FUNDING_ASSET_TYPE_IDS.has(type.id) &&
            type.id !== 'accountBasedPensions'
        ) {
            return;
        }
        const list = factFind.assets?.[type.id];
        if (!list?.length) return;

        list.forEach((asset) => {
            if (!isAssetEligibleForSuggestionRadFunding(factFind, asset)) return;

            const fullValue = asset.value || 0;
            if (fullValue <= 0) return;

            let value = fullValue;
            if (type.id === 'bankAccounts') {
                value = Math.max(0, fullValue - emergencyFundLimit);
            }
            if (value <= 0) return;

            availableAssets.push({
                type: type.id,
                id: asset.id,
                description: asset.description,
                value,
                growthRate: asset.growthRate || (type.id === 'bankAccounts' ? 2 : 5),
                incomeRate:
                    asset.incomeRate ||
                    (type.id === 'bankAccounts' ? 2 : type.id === 'shares' ? 4 : 0),
                fullValue,
            });
        });
    });

    return availableAssets;
}

export function flattenSeRadFundingToManualKeys(radFunding: RadFundingMap = {}): Record<string, number> {
    const manual: Record<string, number> = {};
    Object.entries(radFunding || {}).forEach(([assetType, liquidations]) => {
        const list = Array.isArray(liquidations) ? liquidations : liquidations ? [liquidations] : [];
        list.forEach((liq) => {
            if (!liq || liq.id == null) return;
            const amount = liq.amount || 0;
            if (amount <= 0) return;
            const key = `${assetType}-${liq.id}`;
            manual[key] = (manual[key] || 0) + amount;
        });
    });
    return manual;
}

/** Apply RAD liquidations + optional entity IP sale (matches manual control helpers). */
export function applySuggestionEngineScenarioToFactFind(
    factFind: DraftFactFind,
    scenario: RadFundingScenario | null | undefined,
): DraftFactFind {
    let data: DraftFactFind = structuredClone(factFind);
    const radFunding = scenario?.radFunding || {};
    const manualKeys = flattenSeRadFundingToManualKeys(radFunding);
    if (Object.keys(manualKeys).length > 0) {
        data = applyManualRadFundingToFactFind(data, manualKeys, {});
    }
    const ipDetails = scenario?.strategyOptions?.investmentPropertyDetails;
    if (scenario?.strategyOptions?.sellInvestmentProperty && ipDetails) {
        data = applyInvestmentPropertySaleToFactFind(data, factFind, ipDetails);
    }
    return data;
}

/** Net RAD toward room (liquid draws + IP sale allocation + a reverse mortgage raised to pay it). */
export function computeSuggestionEngineTotalRadPaid(
    factFind: DraftFactFind,
    scenario: RadFundingScenario | null | undefined,
): number {
    const manualKeys = flattenSeRadFundingToManualKeys(scenario?.radFunding);
    const { netTotal: fromLiquid } = computeRadFundingTotals(factFind, manualKeys, {});
    const fromIp =
        scenario?.strategyOptions?.sellInvestmentProperty
            ? scenario?.strategyOptions?.investmentPropertyDetails?.radAllocation || 0
            : 0;
    /*
     * A REVERSE MORTGAGE RAISED FOR THE DEPOSIT IS MONEY PAID TOWARD THE ROOM.
     *
     * This total is what the evaluator uses to strike the daily accommodation payment and the
     * `radPaid` the cost engine assesses, so a source missing from it is a source that quietly does
     * not pay the room. Without this, a candidate named "borrow $400,000 against the home for the
     * room" carried the debt, the interest and the estate cost — and the FULL daily accommodation
     * payment as well, because nothing had been credited against it. The projection was internally
     * consistent and completely wrong, and it would have ranked borrowing as pure loss.
     *
     * `homeEquityRelease.ts` already treats the deposit-absorbed portion as exempt rather than as a
     * countable asset, so this is the other half of a treatment that was only ever half applied.
     * Net of establishment fees, and never more than the room costs.
     */
    const rm = scenario?.strategyOptions?.reverseMortgageDetails as
        | { loanAmount?: number; establishmentFees?: number; useForRad?: boolean; loanStructure?: string }
        | undefined;
    const fromReverseMortgage =
        scenario?.strategyOptions?.reverseMortgage && rm?.useForRad && rm.loanStructure !== 'regular_payments'
            ? Math.max(0, (Number(rm.loanAmount) || 0) - (Number(rm.establishmentFees) || 0))
            : 0;
    const roomPrice = Number(scenario?.strategyOptions?.radInput) || Infinity;
    return Math.min(roomPrice, fromLiquid + fromIp + fromReverseMortgage);
}

export function buildEntityIpSaleDetails(
    factFind: DraftFactFind,
    property: DraftAsset,
    radAllocation: number = 0,
): InvestmentPropertySaleDetails {
    const salePrice = property.value || 0;
    return {
        selectedPropertyId: property.id,
        salePrice,
        sellingCosts: salePrice * 0.025,
        costBase: property.costBase,
        radAllocation,
        newHomePurchasePrice: 0,
        downsizerContribution: 0,
        partnerDownsizerContribution: 0,
        giftingAmount: 0,
        funeralBondPurchase: 0,
    };
}

/**
 * Append whole-entity-IP sale scenarios (include gate only). Subset of RAD % to limit runtime.
 */
export function appendEntityIpSaleScenarios(
    scenarios: RadFundingScenario[],
    factFind: DraftFactFind,
    context: SuggestionEngineScenarioContext,
): RadFundingScenario[] {
    const saleable = getSaleableInvestmentPropertiesForSuggestions(factFind);
    if (!saleable.length) return scenarios;

    const { radAmount, agedCareCosts, cleanCoupleValidation, MPIR, radPercentagesSubset } = context;
    const percentages = radPercentagesSubset || [50, 70, 100];

    saleable.forEach(({ property, isEntity }) => {
        const entityId = isEntity ? getEntityIdFromOwnership(property.ownership) : null;
        const structure = entityId ? (factFind.structures || []).find((s) => s.id === entityId) : undefined;
        const propLabel =
            property.description?.trim() ||
            structure?.name?.trim() ||
            'investment property';

        percentages.forEach((percentage) => {
            const radAmountForThisLevel = radAmount * (percentage / 100);
            const baseDetails = buildEntityIpSaleDetails(factFind, property, 0);
            // DraftFactFind is structurally compatible with FactFind (see
            // types/formDraft.ts); the shared calc-engine fn defensively parseFloat/coerces
            // internally regardless (see factFindTypes.ts header), so this cast is safe.
            const summary = getInvestmentPropertyTaxSummary(factFind as unknown as FactFind, baseDetails);
            const netProceeds = summary?.netProceeds ?? 0;
            const radFromSale = Math.min(netProceeds, radAmountForThisLevel);
            if (radFromSale <= 0) return;

            const remainingRad = radAmountForThisLevel - radFromSale;
            let supplementaryRadFunding: RadFundingMap = {};
            if (remainingRad > 1000) {
                const variants = generateSeFundingVariants(factFind, remainingRad);
                supplementaryRadFunding = variants[0]?.radFunding || {};
                const funded = computeSuggestionEngineTotalRadPaid(factFind, {
                    radFunding: supplementaryRadFunding,
                    strategyOptions: {},
                });
                if (funded < remainingRad - 1000) return;
            }

            const ipDetails = buildEntityIpSaleDetails(factFind, property, radFromSale);
            const dapAmount =
                (radAmount - radAmountForThisLevel) * MPIR +
                (agedCareCosts?.basicDailyFee || 70) * 365 +
                (agedCareCosts?.dailyHSC || 0) * 365;

            const baseStrategy: RadFundingScenario = {
                radFunding: supplementaryRadFunding,
                groupKey: `ip-sale-${property.id}-${percentage}`,
                fundingOrderLabel: isEntity ? 'Entity IP sale' : 'Investment property sale',
                strategyOptions: {
                    annualDap: dapAmount,
                    sellInvestmentProperty: true,
                    investmentPropertyDetails: ipDetails,
                    downsizeHome: false,
                    scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts?.scenarioLivingExpenses, factFind.expenses?.livingExpenses),
                    /* Only an ENTITY sale draws on structure funding. Setting this for a property
                       the client owns outright would tell the engine to reach into a trust that has
                       nothing to do with the sale. */
                    structureFundingIncluded: isEntity,
                },
            };

            scenarios.push({
                ...baseStrategy,
                name: `Pay ${percentage}% RAD, Sell ${propLabel}, Keep Home`,
                groupKey: `${baseStrategy.groupKey}-Keep`,
                strategyOptions: { ...baseStrategy.strategyOptions, rentHome: false },
            });
            scenarios.push({
                ...baseStrategy,
                name: `Pay ${percentage}% RAD, Sell ${propLabel}, Rent Home`,
                groupKey: `${baseStrategy.groupKey}-Rent`,
                strategyOptions: {
                    ...baseStrategy.strategyOptions,
                    rentHome: true,
                    rentAmount: factFind.home?.likelyRent || 1000,
                    rentExpenses: factFind.home?.likelyExpenses || 10000,
                    scenarioLivingExpenses:
                        scenarioLivingExpensesOr(agedCareCosts?.scenarioLivingExpenses, factFind.expenses?.livingExpenses) + (cleanCoupleValidation?.partnerAnnualRent || 0),
                },
            });
        });
    });

    return scenarios;
}

/** Funding-order variants (asset-type liquidation order permutations) for a target RAD amount. */
export function generateSeFundingVariants(
    factFind: DraftFactFind,
    amount: number,
    emergencyFundLimit: number = resolveSeEmergencyFund(factFind),
): Array<{ radFunding: Record<string, LiquidationEntry[]>; orderLabel: string }> {
    const availableAssets = collectLiquidAssetsForSuggestionRad(factFind, emergencyFundLimit);
    if (!availableAssets.length) {
        return [{ radFunding: calculateSeRadFundingForAmount(factFind, amount, emergencyFundLimit), orderLabel: 'Default' }];
    }

    const typesPresent = [...new Set(availableAssets.map((a) => a.type))];
    const typeOrders = permutations(typesPresent);
    const ASSET_TYPE_LABELS: Record<string, string> = {
        bankAccounts: 'Bank',
        accountBasedPensions: 'ABP',
        shares: 'Shares',
        termDeposits: 'Term deposit',
    };
    const results: Array<{ radFunding: Record<string, LiquidationEntry[]>; orderLabel: string }> = [];
    /*
     * WHICH PENSION PAYS (decision 10 / F.46, owner 21 Oct 2026). Every order of a couple's pensions is
     * tried too, not only the lowest-earning first (up to three pensions; above that, lowest-earning first
     * only). Ways that draw the same amounts from the same accounts are kept once.
     */
    const pensionsByRate = availableAssets.filter((a) => a.type === 'accountBasedPensions').sort((a, b) => a.growthRate - b.growthRate);
    const pensionOrders = pensionsByRate.length > 1 && pensionsByRate.length <= 3 ? permutations(pensionsByRate) : [pensionsByRate];
    const seen = new Set<string>();

    for (const typeOrder of typeOrders) {
        for (const pensionOrder of pensionOrders) {
            const sortedAssets: AvailableSuggestionAsset[] = [];
            for (const type of typeOrder) {
                if (type === 'accountBasedPensions') {
                    sortedAssets.push(...pensionOrder);
                } else {
                    sortedAssets.push(...availableAssets.filter((a) => a.type === type).sort((a, b) => b.value - a.value));
                }
            }
            const orderLabel = typeOrder.map((t) => ASSET_TYPE_LABELS[t] || t).join(' -> ')
                + (pensionOrders.length > 1 ? ` (${pensionOrder.map((p) => p.description || p.id).join(', ')})` : '');
            const radFunding = generateSingleSeFundingStrategy(sortedAssets, amount);
            if (!radFunding) continue;
            const sig = Object.keys(radFunding).sort().map((t) => `${t}:${radFunding[t]!.map((e) => `${e.id}=${Math.round(e.amount || 0)}`).join(',')}`).join('|');
            if (pensionOrders.length > 1 && seen.has(sig)) continue;
            seen.add(sig);
            results.push({ radFunding, orderLabel });
        }
    }

    if (results.length === 0) {
        return [{ radFunding: calculateSeRadFundingForAmount(factFind, amount, emergencyFundLimit), orderLabel: 'Default' }];
    }
    return results;
}

export function calculateSeRadFundingForAmount(
    factFind: DraftFactFind,
    amount: number,
    emergencyFundLimit: number = resolveSeEmergencyFund(factFind),
): Record<string, LiquidationEntry[]> {
    const radFunding: Record<string, LiquidationEntry[]> = {};
    let remainingAmount = amount;
    const sorted = collectLiquidAssetsForSuggestionRad(factFind, emergencyFundLimit).sort((a, b) => {
        if (a.type === 'bankAccounts' && b.type !== 'bankAccounts') return -1;
        if (b.type === 'bankAccounts' && a.type !== 'bankAccounts') return 1;
        if (a.type === 'accountBasedPensions' && b.type !== 'accountBasedPensions') return -1;
        if (b.type === 'accountBasedPensions' && a.type !== 'accountBasedPensions') return 1;
        return 0;
    });

    for (const asset of sorted) {
        if (remainingAmount <= 0) break;
        const liquidateAmount = Math.min(remainingAmount, asset.value);
        if (liquidateAmount <= 0) continue;
        if (!radFunding[asset.type]) radFunding[asset.type] = [];
        radFunding[asset.type]!.push({ id: asset.id, amount: liquidateAmount });
        remainingAmount -= liquidateAmount;
    }

    return radFunding;
}

function generateSingleSeFundingStrategy(
    sortedAssets: AvailableSuggestionAsset[],
    amount: number,
): Record<string, LiquidationEntry[]> | null {
    const radFunding: Record<string, LiquidationEntry[]> = {};
    let remainingAmount = amount;

    for (const asset of sortedAssets) {
        if (remainingAmount <= 0) break;
        const liquidateAmount = Math.min(remainingAmount, asset.value);
        if (liquidateAmount <= 0) continue;
        if (!radFunding[asset.type]) radFunding[asset.type] = [];
        radFunding[asset.type]!.push({ id: asset.id, amount: liquidateAmount });
        remainingAmount -= liquidateAmount;
    }

    if (remainingAmount > 1000) return null;
    return radFunding;
}

function permutations<T>(arr: T[]): T[][] {
    if (arr.length <= 1) return [arr];
    const result: T[][] = [];
    for (let i = 0; i < arr.length; i++) {
        const rest = arr.slice(0, i).concat(arr.slice(i + 1));
        for (const p of permutations(rest)) {
            result.push([arr[i]!, ...p]);
        }
    }
    return result;
}
